import { useMemo } from 'react';
import {
  calcularGrauFundamentacao,
  calcularGrauPrecisao,
  validarCoeficienteVariacao,
} from '@/features/laudo/utils/estatisticas-nbr14653';
import {
  validarAreaConstruidaVsTerreno,
  validarFatorAjusteNaoAverbada,
} from '@/features/laudo/utils/validacoes-laudo';
import type { GrausCalculados, Validacao } from './types';

export const useGrausCalculados = (
  numeroAmostrasSaneadas: number,
  coeficienteVariacao: number,
  intervaloConfiabilidade: number
): GrausCalculados => {
  return useMemo(() => {
    const fundamentacao = calcularGrauFundamentacao(
      numeroAmostrasSaneadas,
      true,
      true
    );

    const precisao = calcularGrauPrecisao(intervaloConfiabilidade);
    const cvValido = validarCoeficienteVariacao(coeficienteVariacao);

    return {
      fundamentacao,
      precisao,
      cvValido,
    };
  }, [numeroAmostrasSaneadas, coeficienteVariacao, intervaloConfiabilidade]);
};

export const useValorParcialTerreno = (
  areaTerreno: number,
  valorUnitarioTerreno: number
): number => {
  return useMemo(() => {
    return (areaTerreno || 0) * (valorUnitarioTerreno || 0);
  }, [areaTerreno, valorUnitarioTerreno]);
};

export const useValorParcialConstruida = (
  areaConstruida: number,
  valorUnitarioConstruida: number
): number => {
  return useMemo(() => {
    return (areaConstruida || 0) * (valorUnitarioConstruida || 0);
  }, [areaConstruida, valorUnitarioConstruida]);
};

export const useValorNaoAverbada = (
  especificarAreaNaoAverbada: boolean,
  valorUnitarioConstruida: number,
  fatorAjusteNaoAverbada: number,
  areaNaoAverbada: number
) => {
  const valorUnitarioNaoAverbada = useMemo(() => {
    return (valorUnitarioConstruida || 0) * (fatorAjusteNaoAverbada || 1);
  }, [valorUnitarioConstruida, fatorAjusteNaoAverbada]);

  const valorParcialNaoAverbada = useMemo(() => {
    if (!especificarAreaNaoAverbada) return 0;
    return (areaNaoAverbada || 0) * valorUnitarioNaoAverbada;
  }, [especificarAreaNaoAverbada, areaNaoAverbada, valorUnitarioNaoAverbada]);

  return { valorUnitarioNaoAverbada, valorParcialNaoAverbada };
};

export const useValorTotal = (
  valorParcialTerreno: number,
  valorParcialConstruida: number,
  valorParcialNaoAverbada: number,
  especificarAreaNaoAverbada: boolean
) => {
  const valorTotalSomenteAverbada = useMemo(() => {
    return valorParcialTerreno + valorParcialConstruida;
  }, [valorParcialTerreno, valorParcialConstruida]);

  const valorTotalComNaoAverbada = useMemo(() => {
    if (!especificarAreaNaoAverbada) return valorTotalSomenteAverbada;
    return valorParcialTerreno + valorParcialConstruida + valorParcialNaoAverbada;
  }, [
    valorParcialTerreno,
    valorParcialConstruida,
    valorParcialNaoAverbada,
    especificarAreaNaoAverbada,
    valorTotalSomenteAverbada,
  ]);

  return { valorTotalSomenteAverbada, valorTotalComNaoAverbada };
};

export const useValidacoes = (
  areaConstruida: number,
  areaTerreno: number,
  especificarAreaNaoAverbada: boolean,
  fatorAjusteNaoAverbada: number
): { validacaoArea: Validacao | null; validacaoFatorNaoAverbada: Validacao | null } => {
  const validacaoArea = useMemo(() => {
    return validarAreaConstruidaVsTerreno(areaConstruida, areaTerreno);
  }, [areaConstruida, areaTerreno]);

  const validacaoFatorNaoAverbada = useMemo(() => {
    if (!especificarAreaNaoAverbada) return null;
    return validarFatorAjusteNaoAverbada(fatorAjusteNaoAverbada);
  }, [especificarAreaNaoAverbada, fatorAjusteNaoAverbada]);

  return { validacaoArea, validacaoFatorNaoAverbada };
};
